import React, {Fragment, useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {camelCase, snakeCase} from 'change-case';
import {useRouteUnmountEffect} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {ampVehicleLicensePlateCountryTypes, efVehicleTrailerTypes} from '@ace-de/eua-entity-types';
import {Panel, useStyles, Option, Input, ToggleSwitch} from '@ace-de/ui-components';
import {Form, SelectField, InputField, NumberInputField} from '@ace-de/ui-components/form';
import {germanyFlagImage, otherFlagImage} from '@ace-de/ui-components/assets/flags';
import * as savActionTypes from '../savActionTypes';

// Note: These are hard-coded values per requirements
const manufacturerTypes = [
    'Bürstner',
    'Cabby',
    'Dethleffs',
    'Frendt',
    'Hobby',
    'Hymner',
    'Knaus',
    'LMC',
    'Sonstiges',
    'Tabbert',
    'T. E. C',
    'Weinsberg',
    'Wilk',
];

const CreateTrailerPanel = ({serviceCase, submitSAVTrailerForm}) => {
    const {cx} = useStyles();
    const {translate, createTranslateShorthand} = useTranslate();
    const translateTab = createTranslateShorthand('sav_vehicle_tab');
    const {trailer} = serviceCase;
    const [formData, setFormData] = useState(
        trailer ? {
            ...trailer,
            trailerLicensePlateNumber1: trailer?.licensePlateCountryFlag === ampVehicleLicensePlateCountryTypes.GERMANY
                ? trailer?.licensePlateNumber?.split(' ')?.[0].split('-')[0] : '',
            trailerLicensePlateNumber2: trailer?.licensePlateCountryFlag === ampVehicleLicensePlateCountryTypes.GERMANY
                ? trailer?.licensePlateNumber?.split(' ')?.[0].split('-')[1] : '',
            trailerLicensePlateNumber3: trailer?.licensePlateCountryFlag === ampVehicleLicensePlateCountryTypes.GERMANY
                ? trailer?.licensePlateNumber?.split(' ')?.[1] : '',
        } : null,
    );
    const [hasTrailer, setHasTrailer] = useState(serviceCase.hasTrailer);

    const handleOnTabChange = useCallback(formValues => {
        const serviceCaseData = {
            hasTrailer: hasTrailer,
            trailer: hasTrailer ? {
                ...formValues,
                licensePlateNumber: formValues?.licensePlateCountryFlag === ampVehicleLicensePlateCountryTypes.GERMANY
                    ? (formValues?.trailerLicensePlateNumber1
                        && formValues?.trailerLicensePlateNumber2
                        && formValues?.trailerLicensePlateNumber3
                        ? `${formValues?.trailerLicensePlateNumber1}-${formValues?.trailerLicensePlateNumber2} ${formValues?.trailerLicensePlateNumber3}`
                        : ''
                    ) : formValues?.licensePlateNumber,
            } : null,
        };

        submitSAVTrailerForm({
            serviceCaseId: serviceCase.id,
            serviceCaseData,
            caseType: serviceCase.caseType,
        });
    }, [
        submitSAVTrailerForm,
        serviceCase.id,
        serviceCase.caseType,
        hasTrailer,
    ]);

    useRouteUnmountEffect(({completeRouteUnmountSideEffect}) => {
        if (!formData) {
            completeRouteUnmountSideEffect({
                caller: savActionTypes.SUBMIT_SAV_TRAILER_FORM,
            });
            return;
        }

        handleOnTabChange(formData);
    }, [formData, handleOnTabChange]);

    const toggleEditMode = () => {
        if (hasTrailer) {
            setFormData(null);
        }
        setHasTrailer(prevState => !prevState);
    };

    return (
        <Form name="updateTrailerDetails" onChange={setFormData}>
            {formValues => {
                return (
                    <Fragment>
                        <Panel
                            title={translateTab('create_trailer_panel_title.trailer')}
                            actions={(
                                <ToggleSwitch
                                    name="isToggleTrailerDetailsActive"
                                    value={true}
                                    isSelected={hasTrailer}
                                    onChange={toggleEditMode}
                                />
                            )}
                        >
                            {hasTrailer && (
                                <Fragment>
                                    <div
                                        className={cx([
                                            'global!ace-u-flex',
                                            'global!ace-u-flex--align-flex-end',
                                            'global!ace-u-flex--column-gap-24',
                                            'global!ace-u-margin--bottom-48',
                                        ])}
                                    >
                                        <SelectField
                                            name="type"
                                            value={formData?.type || ''}
                                            label={translateTab('create_trailer_panel_input_label.kind')}
                                            placeholder={translate('global.select.placeholder')}
                                            className={cx('global!ace-u-flex--basis-25')}
                                        >
                                            {Object.keys(efVehicleTrailerTypes).map(key => (
                                                <Option
                                                    key={key}
                                                    name={`trailerType${camelCase(key)}`}
                                                    value={key}
                                                >
                                                    {translate(`global.trailer_type.${snakeCase(efVehicleTrailerTypes[key])}`)}
                                                </Option>
                                            ))}
                                        </SelectField>
                                        <SelectField
                                            name="manufacturer"
                                            value={formData?.manufacturer || ''}
                                            label={translateTab('create_trailer_panel_input_label.manufacturer')}
                                            placeholder={translate('global.select.placeholder')}
                                            className={cx('global!ace-u-flex--basis-25')}
                                        >
                                            {manufacturerTypes.map(manufacturerType => (
                                                <Option
                                                    key={manufacturerType}
                                                    name={`manufacturerType${manufacturerType}`}
                                                    value={manufacturerType}
                                                >
                                                    {manufacturerType}
                                                </Option>
                                            ))}
                                        </SelectField>
                                        <InputField
                                            name="model"
                                            value={formData?.model || ''}
                                            label={translateTab('create_trailer_panel_input_label.type_model')}
                                            placeholder={translate('global.select.placeholder')}
                                            className={cx('global!ace-u-flex--basis-30')}
                                        />
                                    </div>
                                    <div
                                        className={cx([
                                            'global!ace-u-flex',
                                            'global!ace-u-flex--align-flex-end',
                                            'global!ace-u-flex--column-gap-24',
                                            'global!ace-u-margin--bottom-48',
                                        ])}
                                    >
                                        <SelectField
                                            name="licensePlateCountryFlag"
                                            value={formData?.licensePlateCountryFlag
                                                ? (formData?.licensePlateCountryFlag !== ampVehicleLicensePlateCountryTypes.GERMANY // eslint-disable-line max-len
                                                    ? ampVehicleLicensePlateCountryTypes.OTHER
                                                    : ampVehicleLicensePlateCountryTypes.GERMANY
                                                ) : ampVehicleLicensePlateCountryTypes.GERMANY}
                                            label={translateTab('create_trailer_panel_input_label.license_plate_no')}
                                            placeholder={translate('global.select.placeholder')}
                                            className={cx('global!ace-u-flex--basis-10')}
                                        >
                                            <Option
                                                key="germanyFlagImage"
                                                name="germanyFlagImage"
                                                value={ampVehicleLicensePlateCountryTypes.GERMANY}
                                                selectedLabel={(
                                                    <img src={germanyFlagImage} alt="germanyFlagImage" />
                                                )}
                                            >
                                                <img src={germanyFlagImage} alt="germanyFlagImage" />
                                                <span className={cx('global!ace-u-margin--left-8')}>
                                                    {translate(`global.country.germany`)}
                                                </span>
                                            </Option>
                                            <Option
                                                key="otherFlagImage"
                                                name="otherFlagImage"
                                                value={ampVehicleLicensePlateCountryTypes.OTHER}
                                                selectedLabel={(
                                                    <img src={otherFlagImage} alt="otherFlagImage" />
                                                )}
                                            >
                                                <img src={otherFlagImage} alt="otherFlagImage" />
                                                <span className={cx('global!ace-u-margin--left-8')}>
                                                    {translate(`global.country.other`)}
                                                </span>
                                            </Option>
                                        </SelectField>
                                        {formValues.licensePlateCountryFlag === ampVehicleLicensePlateCountryTypes.GERMANY // eslint-disable-line max-len
                                            ? (
                                                <Fragment>
                                                    <Input
                                                        name="trailerLicensePlateNumber1"
                                                        value={formData?.trailerLicensePlateNumber1 || ''}
                                                        className={cx('global!ace-u-flex--basis-10')}
                                                    />
                                                    <Input
                                                        name="trailerLicensePlateNumber2"
                                                        value={formData?.trailerLicensePlateNumber2 || ''}
                                                        className={cx('global!ace-u-flex--basis-10')}
                                                    />
                                                    <Input
                                                        name="trailerLicensePlateNumber3"
                                                        value={formData?.trailerLicensePlateNumber3 || ''}
                                                        className={cx('global!ace-u-flex--basis-10')}
                                                    />
                                                </Fragment>
                                            ) : (
                                                <Input
                                                    name="licensePlateNumber"
                                                    value={formData?.licensePlateNumber || ''}
                                                    className={cx('global!ace-u-flex--basis-30')}
                                                />
                                            )}
                                        <InputField
                                            name="notes"
                                            value={formData?.notes || ''}
                                            label={translateTab('create_trailer_panel_input_label.additional_comments')}
                                            placeholder={translate('global.select.placeholder')}
                                            className={cx('global!ace-u-flex--basis-60')}
                                        />
                                    </div>
                                    <div
                                        className={cx([
                                            'global!ace-u-flex',
                                            'global!ace-u-flex--align-flex-end',
                                            'global!ace-u-flex--column-gap-24',
                                            'global!ace-u-margin--bottom-48',
                                        ])}
                                    >
                                        <NumberInputField
                                            name="weight"
                                            value={formData?.weight || ''}
                                            label={translateTab('create_trailer_panel_input_label.weight')}
                                            min={0}
                                        />
                                    </div>
                                    <div
                                        className={cx([
                                            'global!ace-u-flex',
                                            'global!ace-u-flex--align-flex-end',
                                            'global!ace-u-flex--column-gap-24',
                                        ])}
                                    >
                                        <NumberInputField
                                            name="length"
                                            value={formData?.length || ''}
                                            label={translateTab('create_trailer_panel_input_label.length')}
                                            min={0}
                                        />
                                        <NumberInputField
                                            name="width"
                                            value={formData?.width || ''}
                                            label={translateTab('create_trailer_panel_input_label.width')}
                                            min={0}
                                        />
                                        <NumberInputField
                                            name="height"
                                            value={formData?.height || ''}
                                            label={translateTab('create_trailer_panel_input_label.height')}
                                            min={0}
                                        />
                                    </div>
                                </Fragment>
                            )}
                        </Panel>
                    </Fragment>
                );
            }}
        </Form>
    );
};

CreateTrailerPanel.propTypes = {
    serviceCase: PropTypes.object,
    submitSAVTrailerForm: PropTypes.func.isRequired,
};

CreateTrailerPanel.defaultProps = {
    serviceCase: {},
};

const mapDispatchToProps = dispatch => ({
    submitSAVTrailerForm: payload => dispatch({
        type: savActionTypes.SUBMIT_SAV_TRAILER_FORM,
        payload,
    }),
});

export default connect(null, mapDispatchToProps)(CreateTrailerPanel);
